import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Form, 
  Button, 
  Alert,
  Image
} from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FaSave, FaArrowLeft, FaTrash, FaStar, FaImage } from 'react-icons/fa';
import { servicesAPI } from '../services/api';
import { Formik, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import * as Icons from 'react-icons/fa';
import Icon from '../components/Icon';
import { AxiosResponse } from 'axios';

interface ServiceFormValues {
  title: string;
  titleAr: string;
  description: string;
  descriptionAr: string;
}

interface servicesAPI {
  getServiceById: (id: string) => Promise<AxiosResponse>;
  createService: (formData: FormData) => Promise<AxiosResponse>;
  updateService: (id: string, formData: FormData) => Promise<AxiosResponse>;
  deleteServiceImage: (serviceId: string, imageId: string) => Promise<AxiosResponse>;
  setMainServiceImage: (serviceId: string, imageId: string) => Promise<AxiosResponse>;
  deleteServiceCatalog: (serviceId: string) => Promise<AxiosResponse>;
  uploadServiceCatalog: (serviceId: string, formData: FormData) => Promise<AxiosResponse>;
  downloadServiceCatalog: (serviceId: string) => Promise<AxiosResponse>;
}

const typedServicesAPI = servicesAPI as unknown as servicesAPI;

// Validation schema
const ServiceSchema = Yup.object().shape({
  title: Yup.string().required('Title is required'),
  titleAr: Yup.string(),
  description: Yup.string().required('Description is required'),
  descriptionAr: Yup.string(),
});

const ServiceForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  
  const [loading, setLoading] = useState(isEditMode);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [images, setImages] = useState<File[]>([]);
  const [existingImages, setExistingImages] = useState<{id: string, url: string, isMain?: boolean}[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [catalogFile, setCatalogFile] = useState<File | null>(null);
  const [existingCatalog, setExistingCatalog] = useState<{path: string, description: string} | null>(null);
  
  // Change initialValues from a constant to a state variable
  const [formValues, setFormValues] = useState<ServiceFormValues>({
    title: '',
    titleAr: '',
    description: '',
    descriptionAr: '',
  });

  // Fetch service data if in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      const fetchService = async () => {
        try {
          const response = await servicesAPI.getServiceById(id);
          const service = response.data;
          
          // Update the form values state instead of modifying a constant
          setFormValues({
            title: service.name,
            titleAr: service.nameAr,
            description: service.description,
            descriptionAr: service.descriptionAr
          });
          
          // Set existing images
          if (service.images && service.images.length > 0) {
            const formattedImages = service.images.map((img: any) => ({
              id: img._id || img.id,
              url: `${img.path || img}`,
              isMain: img.isMain
            }));
            setExistingImages(formattedImages);
          }
          
          // Set existing catalog if available
          if (service.catalog && service.catalog.file) {
            setExistingCatalog({
              path: `${process.env.REACT_APP_API_URL_WITHOUT_API}/${service.catalog.file}`,
              description: service.catalog.description || 'Service Catalog'
            });
          }
          
          setLoading(false);
        } catch (err: any) {
          setError('Failed to fetch service details');
          console.error(err);
          setLoading(false);
        }
      };
      
      fetchService();
    } else {
      setLoading(false);
    }
  }, [id, isEditMode]);

  // Handle form submission
  const handleSubmit = async (values: ServiceFormValues, { setSubmitting }: any) => {
    setError('');
    setSuccess('');
    setSubmitting(true);
    
    try {
      const formData = new FormData();
      formData.append('name', values.title);
      formData.append('nameAr', values.titleAr);
      formData.append('description', values.description);
      formData.append('descriptionAr', values.descriptionAr);
      
      // Append new images
      images.forEach(image => {
        formData.append('images', image);
      });

      // Append catalog file if exists
      // Append catalog file if exists
      if (catalogFile) {
        formData.append('catalogFile', catalogFile);
      } else if (existingCatalog) {
        // Include catalog description even if not uploading a new file
      }
      
      let response;
      if (isEditMode && id) {
        response = await servicesAPI.updateService(id, formData);
        setSuccess('Service updated successfully');
      } else {
        response = await servicesAPI.createService(formData);
        setSuccess('Service created successfully');
      }
      
      // Navigate back after a short delay
      setTimeout(() => {
        navigate('/services');
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save service');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle image file selection
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      setImages(prev => [...prev, ...selectedFiles]);
      
      // Create previews for the new images
      const newPreviews = selectedFiles.map(file => URL.createObjectURL(file));
      setImagePreviews(prev => [...prev, ...newPreviews]);
    }
  };

  // Remove selected image
  const removeSelectedImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    
    // Revoke object URL to avoid memory leaks
    URL.revokeObjectURL(imagePreviews[index]);
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  // Delete existing image
  const deleteExistingImage = async (imageId: string) => {
    if (!id) return;
    
    try {
      await servicesAPI.deleteServiceImage(id, imageId);
      setExistingImages(prev => prev.filter(img => img.id !== imageId));
      setSuccess('Image deleted successfully');
      
      // Clear success message after a delay
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err: any) {
      setError('Failed to delete image');
      console.error(err);
    }
  };

  // Set main service image
  const setMainImage = async (imageId: string) => {
    if (!id) return;
    
    try {
      await servicesAPI.setMainServiceImage(id, imageId);
      
      // Update local state to reflect the change
      setExistingImages(prev => 
        prev.map(img => ({
          ...img,
          isMain: img.id === imageId
        }))
      );
      
      setSuccess('Main image updated successfully');
      
      // Clear success message after a delay
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err: any) {
      setError('Failed to update main image');
      console.error(err);
    }
  };

  const handleCatalogChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setCatalogFile(e.target.files[0]);
    }
  };

  // Delete existing catalog
  const deleteExistingCatalog = async () => {
    if (!id) return;
    
    try {
      await typedServicesAPI.deleteServiceCatalog(id);
      setExistingCatalog(null);
      setSuccess('Catalog deleted successfully');
      
      // Clear success message after a delay
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err: any) {
      setError('Failed to delete catalog');
      console.error(err);
    }
  };

  // Handle catalog upload separately if needed
  const handleCatalogUpload = async (productId: string) => {
    if (!catalogFile) return;
    
    try {
      const formData = new FormData();
      formData.append('catalog', catalogFile);
      
      await typedServicesAPI.uploadServiceCatalog(productId, formData);
      setSuccess('Catalog uploaded successfully');
    } catch (err: any) {
      setError('Failed to upload catalog');
      console.error(err);
    }
  };

  // Download catalog
  const downloadCatalog = async (productId: string) => {
    try {
      const response = await typedServicesAPI.downloadServiceCatalog(productId);
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'product-catalog.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err: any) {
      setError('Failed to download catalog');
      console.error(err);
    }
  };


  if (loading) {
    return (
      <Container>
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading service data...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row className="mb-4 align-items-center">
        <Col>
          <h1 className="mb-0">{isEditMode ? 'Edit Service' : 'Add New Service'}</h1>
        </Col>
        <Col xs="auto">
          <Link to="/services">
            <Button variant="outline-secondary">
              <Icon icon={Icons.FaArrowLeft} className="me-2" /> Back to Services
            </Button>
          </Link>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <Card className="border-0 shadow-sm">
        <Card.Body>
          <Formik
            initialValues={formValues}
            validationSchema={ServiceSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
            }) => (
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col lg={8}>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Title (English)</Form.Label>
                          <Form.Control
                            type="text"
                            name="title"
                            value={values.title}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.title && !!errors.title}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.title}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Title (Arabic)</Form.Label>
                          <Form.Control
                            type="text"
                            name="titleAr"
                            value={values.titleAr}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.titleAr && !!errors.titleAr}
                            dir="rtl"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.titleAr}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>Description (English)</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={5}
                        name="description"
                        value={values.description}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.description && !!errors.description}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.description}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Description (Arabic)</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={5}
                        name="descriptionAr"
                        value={values.descriptionAr}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.descriptionAr && !!errors.descriptionAr}
                        dir="rtl"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.descriptionAr}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  
                  <Col lg={4}>
                    <Card className="bg-light">
                      <Card.Body>
                        <h5 className="mb-3">Service Images</h5>
                        
                        {/* Existing Images */}
                        {existingImages.length > 0 && (
                          <div className="mb-3">
                            <p className="text-muted mb-2">Current Images</p>
                            <Row xs={2} className="g-2 mb-3">
                              {existingImages.map((img) => (
                                <Col key={img.id}>
                                  <div className="position-relative">
                                    <Image 
                                      src={img.url} 
                                      alt="Service" 
                                      className="img-thumbnail" 
                                      style={{ height: '100px', objectFit: 'cover' }}
                                    />
                                    <div className="position-absolute top-0 end-0 d-flex">
                                      {img.isMain ? (
                                        <span className="badge bg-primary">
                                          <Icon icon={Icons.FaStar} /> Main
                                        </span>
                                      ) : (
                                        <Button 
                                          variant="light" 
                                          size="sm" 
                                          className="p-1"
                                          onClick={() => setMainImage(img.id)}
                                          title="Set as main image"
                                        >
                                          <Icon icon={Icons.FaStar} />
                                        </Button>
                                      )}
                                      <Button 
                                        variant="danger" 
                                        size="sm" 
                                        className="p-1 ms-1"
                                        onClick={() => deleteExistingImage(img.id)}
                                        title="Delete image"
                                      >
                                        <Icon icon={Icons.FaTrash} />
                                      </Button>
                                    </div>
                                  </div>
                                </Col>
                              ))}
                            </Row>
                          </div>
                        )}
                        
                        {/* New Images */}
                        <div className="mb-3">
                          <p className="text-muted mb-2">Add New Images</p>
                          <Form.Control 
                            type="file" 
                            onChange={handleImageChange} 
                            accept="image/*"
                            multiple
                          />
                          <Form.Text className="text-muted">
                            Select up to 5 images. Maximum file size: 5MB.
                          </Form.Text>
                        </div>
                        
                        {/* New Image Previews */}
                        {imagePreviews.length > 0 && (
                          <div className="mb-3">
                            <p className="text-muted mb-2">New Images Preview</p>
                            <Row xs={2} className="g-2">
                              {imagePreviews.map((preview, index) => (
                                <Col key={index}>
                                  <div className="position-relative">
                                    <Image 
                                      src={preview} 
                                      alt={`Preview ${index + 1}`} 
                                      className="img-thumbnail" 
                                      style={{ height: '100px', objectFit: 'cover' }}
                                    />
                                    <Button 
                                      variant="danger" 
                                      size="sm" 
                                      className="position-absolute top-0 end-0 p-1"
                                      onClick={() => removeSelectedImage(index)}
                                      title="Remove image"
                                    >
                                      <Icon icon={Icons.FaTrash} />
                                    </Button>
                                  </div>
                                </Col>
                              ))}
                            </Row>
                          </div>
                        )}
                        
                        {/* No Images Info */}
                        {existingImages.length === 0 && imagePreviews.length === 0 && (
                          <div className="text-center p-4 bg-white rounded mb-3">
                            <Icon icon={Icons.FaImage} className="display-4 text-muted mb-3" />
                            <p>No images selected</p>
                          </div>
                        )}
                      </Card.Body>
                    </Card>

                    <Card className="bg-light mt-3">
                      <Card.Body>
                        <h5 className="mb-3">Product Catalog (PDF)</h5>
                        
                        {/* Existing Catalog */}
                        {existingCatalog && (
                          <div className="mb-3">
                            <p className="text-muted mb-2">Current Catalog</p>
                            <div className="d-flex align-items-center bg-white p-2 rounded">
                              <Icon icon={Icons.FaFilePdf} className="text-danger me-2" style={{ fontSize: '24px' }} />
                              <div className="flex-grow-1">
                                <div>Product Catalog</div>
                                <small className="text-muted">
                                  {existingCatalog.description || 'PDF Document'}
                                </small>
                              </div>
                              <a href={existingCatalog.path} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary me-2">
                                <Icon icon={Icons.FaEye} /> View
                              </a>
                              <Button 
                                variant="outline-secondary" 
                                size="sm" 
                                className="me-2"
                                onClick={() => id && downloadCatalog(id)}
                                title="Download catalog"
                              >
                                <Icon icon={Icons.FaDownload} />
                              </Button>
                              <Button 
                                variant="danger" 
                                size="sm" 
                                className="p-1"
                                onClick={deleteExistingCatalog}
                                title="Delete catalog"
                              >
                                <Icon icon={Icons.FaTrash} />
                              </Button>
                            </div>
                          </div>
                        )}
                        
                        {/* New Catalog Upload */}
                        <div className="mb-3">
                          <p className="text-muted mb-2">{existingCatalog ? 'Replace Catalog' : 'Add Catalog'}</p>
                          <Form.Control 
                            type="file" 
                            onChange={handleCatalogChange} 
                            accept=".pdf"
                          />
                          <Form.Text className="text-muted">
                            Upload a PDF catalog for this product. Maximum file size: 5MB.
                          </Form.Text>
                        </div>
                        
                        {/* Catalog File Preview */}
                        {catalogFile && (
                          <div className="mb-3">
                            <p className="text-muted mb-2">Selected Catalog</p>
                            <div className="d-flex align-items-center bg-white p-2 rounded">
                              <Icon icon={Icons.FaFilePdf} className="text-danger me-2" style={{ fontSize: '24px' }} />
                              <div className="flex-grow-1">
                                <div>{catalogFile.name}</div>
                                <small className="text-muted">{(catalogFile.size / 1024).toFixed(2)} KB</small>
                              </div>
                              <Button 
                                variant="danger" 
                                size="sm" 
                                className="p-1"
                                onClick={() => setCatalogFile(null)}
                                title="Remove catalog"
                              >
                                <Icon icon={Icons.FaTrash} />
                              </Button>
                            </div>
                          </div>
                        )}
                        
                        {/* No Catalog Info */}
                        {!existingCatalog && !catalogFile && (
                          <div className="text-center p-4 bg-white rounded mb-3">
                            <Icon icon={Icons.FaFilePdf} className="display-4 text-muted mb-3" />
                            <p>No catalog selected</p>
                          </div>
                        )}
                      </Card.Body>
                    </Card>

                  </Col>
                </Row>
                
                <div className="d-flex justify-content-end mt-4">
                  <Button 
                    variant="secondary" 
                    as={Link as any} 
                    to="/services" 
                    className="me-2"
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="primary" 
                    type="submit" 
                    disabled={isSubmitting}
                  >
                    <Icon icon={Icons.FaSave} className="me-2" />
                    {isSubmitting ? 'Saving...' : 'Save Service'}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ServiceForm; 